import { Box } from "@mui/material"
import { GridColDef } from "@mui/x-data-grid"
import DataGridComponent from "components/DataGridComponent/DataGridComponent"
import { DeleteDialog } from "components/DeleteDialog/DeleteDialog"
import { QRCodeGenerator } from "components/QRCodeGenerator/QRCodeGenerator"
import { Device, DeviceTestEquipment, DeviceType } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useMemo, useState } from "react"
import { AddTestEquipmentDialog } from "./AddTestEquipmentDialog"

const emptyTestEquipment: DeviceTestEquipment = {
  id: "",
  type: DeviceType.TEST_EQUIPMENT,
  barcode: "",
  images: [],
  documents: [],
  name: "",
  description: "",
  manufacturer: "",
  inventoryNo: "",
  calibrationDue: "",
  uncertainty: "",
  teType: "",
  calibrationDate: "",
  status: "",
  location: "",
}

type DeviceTestEquipmentRow = DeviceTestEquipment & {
  descriptionDisplay: string
  typeModelDisplay: string
  manufacturerDisplay: string
  inventoryNumberDisplay: string
  calibrationDateDisplay: string
  calibrationDueDisplay: string
  statusDisplay: string
  locationDisplay: string
  isPrimas: boolean
}

const FALLBACK_PLACEHOLDER = "./."

const formatDisplayValue = (value?: string | null) =>
  value && value.trim().length > 0 ? value : FALLBACK_PLACEHOLDER

const formatDateValue = (value?: string | null) => {
  if (!value || value.trim().length === 0) {
    return FALLBACK_PLACEHOLDER
  }

  const parsed = Date.parse(value)
  if (Number.isNaN(parsed)) {
    return value
  }

  return new Date(parsed).toLocaleDateString("de-DE")
}

const AdminTestEquipment = () => {
  const testEquipment = useStoreState((state) => state.testEquipment)
  const { updateTestEquipment, deleteTestEquipment, fetchTestEquipment } = useStoreActions(
    (actions) => actions
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formState, setFormState] = useState<DeviceTestEquipment>(emptyTestEquipment)
  const [openDelete, setOpenDelete] = useState(false)
  const [toDelete, setToDelete] = useState<string[]>([])
  const [openQRCodeGenerator, setOpenQRCodeGenerator] = useState(false)
  const [activeOnly, setActiveOnly] = useState<boolean>(false)

  useEffect(() => {
    fetchTestEquipment()
  }, [fetchTestEquipment])

  const enrichedTestEquipment = useMemo<DeviceTestEquipmentRow[]>(() => {
    const filtered = testEquipment.filter((te) => te.type === DeviceType.TEST_EQUIPMENT)

    const activeFiltered = filtered.filter((te) => {
      if (!activeOnly) {
        return true
      }

      const status = (te as DeviceTestEquipment).status
      return status == null || status === "Aktiv"
    })

    return activeFiltered.map((device) => {
      const te = device as DeviceTestEquipment
      const rawDescription = te.description ? te.description.trim() : ""
      const descriptionIsFallback = rawDescription.toLowerCase() === "primas equipment"
      const primaryDescription =
        rawDescription.length > 0 && !descriptionIsFallback ? rawDescription : te.name
      const isPrimas = !te.id.includes("-")
      return {
        ...te,
        descriptionDisplay: formatDisplayValue(primaryDescription || te.name),
        typeModelDisplay: formatDisplayValue(te.model || te.teType),
        manufacturerDisplay: formatDisplayValue(te.manufacturer),
        inventoryNumberDisplay: formatDisplayValue(
          te.inventoryNo && te.inventoryNo.trim().length > 0 ? te.inventoryNo : te.barcode
        ),
        calibrationDateDisplay: formatDateValue(te.calibrationDate),
        calibrationDueDisplay: formatDateValue(te.calibrationDue),
        statusDisplay: formatDisplayValue(te.status),
        locationDisplay: formatDisplayValue(te.location),
        isPrimas,
      }
    })
  }, [testEquipment, activeOnly])

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: "barcode", headerName: "Barcode", width: 130 },
      { field: "descriptionDisplay", headerName: "Beschreibung", flex: 1, minWidth: 220 },
      { field: "typeModelDisplay", headerName: "Typ / Modell", flex: 0.8, minWidth: 160 },
      { field: "manufacturerDisplay", headerName: "Hersteller", flex: 0.8, minWidth: 150 },

      // {
      //   field: "calibrationDateDisplay",
      //   headerName: "Kalibriert am",
      //   flex: 0.6,
      //   minWidth: 140,
      // },
      // {
      //   field: "calibrationDueDisplay",
      //   headerName: "Kalibrierung fällig",
      //   flex: 0.7,
      //   minWidth: 160,
      // },
      { field: "statusDisplay", headerName: "Status", flex: 0.6, minWidth: 130 },
      { field: "locationDisplay", headerName: "Standort", flex: 0.6, minWidth: 130 },
    ],
    []
  )

  const handleEdit = (id: string) => {
    if (id === "NEW") {
      setFormState({
        ...emptyTestEquipment,
        id: "NEW",
      })
    } else {
      const te = testEquipment.find((tb) => tb.id === id)
      if (te) {
        setFormState(te as DeviceTestEquipment)
      }
    }
    setDialogOpen(true)
  }

  const handleDelete = (id: string[]) => {
    setOpenDelete(true)
    setToDelete(id)
  }

  const handleDeleteSelected = () => {
    deleteTestEquipment(toDelete)
    setToDelete([])
    setOpenDelete(false)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
  }

  const handleFormSubmit = (arg: DeviceTestEquipment) => {
    updateTestEquipment(arg)
    setDialogOpen(false)
  }

  const handleQRCode = (row: Device) => {
    setOpenQRCodeGenerator(true)
    setFormState(row as DeviceTestEquipment)
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ mt: 2 }}>
          <DataGridComponent
            columns={columns}
            rows={enrichedTestEquipment}
            getRowId={(row) => row.id}
            onEdit={handleEdit}
            onAdd={() => handleEdit("NEW")}
            onDelete={handleDelete}
            onQRCode={handleQRCode}
            enablePagination
            initialPageSize={50}
            pageSizeOptions={[25, 50, 100, 250]}
            canEditRow={(row) => !row.isPrimas}
            canDeleteRow={(row) => !row.isPrimas}
          />
        </Box>
      </Box>

      <AddTestEquipmentDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        formState={formState}
        onSubmit={handleFormSubmit}
      />

      <DeleteDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onDelete={handleDeleteSelected}
      />

      <QRCodeGenerator
        open={openQRCodeGenerator}
        onClose={() => setOpenQRCodeGenerator(false)}
        barcode={formState.barcode}
        title={formState.name}
      />
    </>
  )
}

export default AdminTestEquipment
